import { Component } from '@theme/component';
import { applyPropertiesToPayload, resolveProperties } from '@theme/cart-attribution';

class KcArticleToc extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLHeadingElement[]} */
  #headings = [];

  /** Scroll-spy is suppressed while a click-triggered smooth scroll is in flight. */
  #suppressSpy = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #spyResumeTimer;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    // The custom element can upgrade before its children are parsed (Horizon bundles
    // section JS early), which leaves `this.refs` empty and the TOC never builds.
    // Defer TOC/toggle init until the article body is actually present in the DOM.
    if (this.querySelector('.kc-article__body')) {
      this.#init(signal);
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#init(signal), { once: true, signal });
    } else {
      requestAnimationFrame(() => this.#init(signal));
    }
  }

  /**
   * Build the TOC and wire the mobile toggle once children are available.
   * @param {AbortSignal} signal
   */
  #init(signal) {
    this.#buildToc(signal);
    this.#setupMobileToggle(signal);
    this.#markActiveCategoryPill();
  }

  /**
   * Highlight the quick-search pill whose label matches this article's
   * `category:` tag. The category is surfaced server-side in the
   * `.js-article-breadcrumb-tag` span. The generic <article-quick-search>
   * component only marks pills active by URL, which never matches on an
   * article detail page, so the tag-based match is done here.
   *
   * The pill class survives <article-quick-search>'s node replacement in
   * either order (it copies every attribute, including class, onto the new
   * anchor), so this can run before or after that component upgrades.
   */
  #markActiveCategoryPill() {
    const tagEl = this.querySelector('.js-article-breadcrumb-tag');
    const category = tagEl?.textContent.trim().toLowerCase();
    if (!category) return;

    const bar = document.querySelector('.article-quick-search');
    if (!bar) return;

    for (const pill of bar.querySelectorAll('.pill')) {
      const labelEl = pill.querySelector('.pill__label');
      const label = (labelEl?.textContent || pill.textContent).trim().toLowerCase();
      if (label && label === category) {
        pill.classList.add('pill--active');
        pill.setAttribute('aria-current', 'true');
      }
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#spyResumeTimer);
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Parse all H2 elements inside the article body, generate slugified IDs,
   * and inject corresponding links into both desktop and mobile TOC containers.
   * @param {AbortSignal} signal
   */
  #buildToc(signal) {
    const articleBody = /** @type {Element | null} */ (
      this.refs.articleBody || this.querySelector('.kc-article__body')
    );
    if (!articleBody) return;

    // Only promote bold-paragraph "headings" when the article has no real <h2>s.
    // In a mixed article the author used real headings, so leave bold paragraphs as-is.
    if (!articleBody.querySelector('h2')) {
      this.#promoteBoldParagraphsToHeadings(articleBody);
    }

    const headings = articleBody.querySelectorAll('h2');
    if (headings.length === 0) {
      this.#hideTocContainers();
      return;
    }

    // Liquid server-renders the real-<h2> links (SEO / no-JS); rebuild the lists
    // here so the promoted bold-paragraph headings are included too.
    const lists = /** @type {Element[]} */ (this.refs.tocList || []);
    for (const list of lists) list.replaceChildren();

    for (const heading of headings) {
      const text = heading.textContent.trim();
      if (!text) continue;

      if (!heading.id) heading.id = this.#slugify(text);

      for (const list of lists) {
        const item = document.createElement('li');
        item.className = 'kc-article__toc-item';
        item.appendChild(this.#createTocLink(heading.id, text, 'kc-article__toc-link'));
        list.appendChild(item);
      }
    }

    this.#setupScrollSpy(headings, signal);
  }

  /**
   * Merchants author section headings as bold paragraphs (`<p><strong>…</strong></p>`)
   * rather than real `<h2>` elements. Promote those to `<h2>` so they render as
   * headings and populate the "In this article" nav. Guarded so ordinary body copy,
   * list items, and long fully-bold paragraphs are never promoted.
   * @param {Element} articleBody
   */
  #promoteBoldParagraphsToHeadings(articleBody) {
    const paragraphs = Array.from(articleBody.querySelectorAll('p'));
    for (const p of paragraphs) {
      const strong = p.querySelector('strong, b');
      const text = p.textContent.trim();
      const isHeadingLike =
        strong &&
        p.children.length === 1 &&
        p.children[0] === strong &&
        strong.textContent.trim() === text &&
        text.length > 0 &&
        text.length <= 120;

      if (isHeadingLike) {
        const h2 = document.createElement('h2');
        h2.textContent = text;
        p.replaceWith(h2);
      }
    }
  }

  /**
   * Create a TOC anchor element.
   * @param {string} slug
   * @param {string} text
   * @param {string} className
   * @returns {HTMLAnchorElement}
   */
  #createTocLink(slug, text, className) {
    const link = document.createElement('a');
    link.href = `#${slug}`;
    link.className = className;
    link.textContent = text;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      this.#scrollToHeading(slug);
      this.#setActiveLink(slug);
      this.#closeMobilePanel();
      // Hold the clicked link active while the smooth scroll runs, so scroll-spy
      // doesn't flip it through the headings it passes. Resumes on scrollend
      // (with a timeout fallback for browsers without it).
      this.#suppressSpy = true;
      clearTimeout(this.#spyResumeTimer);
      this.#spyResumeTimer = setTimeout(() => this.#resumeSpy(), 1500);
    });
    return link;
  }

  /**
   * Slugify a heading text string.
   * @param {string} text
   * @returns {string}
   */
  #slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Scroll-spy via scroll position (not IntersectionObserver): highlight the last
   * heading scrolled past, so the active item is correct even when the viewport
   * sits below a heading. Suppressible during a click-triggered smooth scroll.
   * @param {NodeListOf<HTMLHeadingElement>} headings
   * @param {AbortSignal} signal
   */
  #setupScrollSpy(headings, signal) {
    this.#headings = [...headings];
    const onScroll = () => {
      if (!this.#suppressSpy) this.#updateActiveHeading();
    };
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', onScroll, { passive: true, signal });
    // The click-scroll is done → let scroll-spy take over again (no re-sync, so the
    // clicked link stays active until the reader actually scrolls).
    window.addEventListener('scrollend', () => this.#resumeSpy(), { passive: true, signal });
    this.#updateActiveHeading();
  }

  /** Highlight the link for the last heading whose top has passed the activation line. */
  #updateActiveHeading() {
    if (this.#headings.length === 0) return;
    const line = 120;
    let active = this.#headings[0];
    for (const heading of this.#headings) {
      if (heading.getBoundingClientRect().top <= line) active = heading;
      else break;
    }
    if (active) this.#setActiveLink(active.id);
  }

  /** Re-enable scroll-spy after a click-triggered scroll settles. */
  #resumeSpy() {
    this.#suppressSpy = false;
    clearTimeout(this.#spyResumeTimer);
  }

  /**
   * Set the active TOC link by slug in both desktop and mobile containers.
   * @param {string} activeSlug
   */
  #setActiveLink(activeSlug) {
    const allLinks = this.querySelectorAll('.kc-article__toc-link');
    for (const link of allLinks) {
      if (link.getAttribute('href') === `#${activeSlug}`) {
        link.classList.add('is-active');
      } else {
        link.classList.remove('is-active');
      }
    }
  }

  /**
   * Smooth scroll to a heading by its slug ID.
   * @param {string} slug
   */
  #scrollToHeading(slug) {
    const target = document.getElementById(slug);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Hide TOC containers when no headings are found.
   */
  #hideTocContainers() {
    const desktopTocSection = this.querySelector('.kc-article__toc-desktop');
    const mobileTocSection = this.querySelector('.kc-article__toc-mobile');
    if (desktopTocSection) desktopTocSection.style.display = 'none';
    if (mobileTocSection) mobileTocSection.style.display = 'none';
  }

  /**
   * Setup mobile TOC toggle.
   * @param {AbortSignal} signal
   */
  #setupMobileToggle(signal) {
    const toggleBtn = this.refs.tocToggle || this.querySelector('[ref="tocToggle"]');
    if (!toggleBtn) return;

    toggleBtn.addEventListener('click', () => {
      const panel = this.refs.tocMobile || this.querySelector('[ref="tocMobile"]');
      if (!panel) return;

      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        this.#closeMobilePanel();
      } else {
        toggleBtn.setAttribute('aria-expanded', 'true');
        panel.style.maxHeight = `${panel.scrollHeight}px`;
        panel.classList.add('is-open');
      }
    }, { signal });
  }

  /**
   * Close the mobile TOC panel.
   */
  #closeMobilePanel() {
    const toggleBtn = this.refs.tocToggle || this.querySelector('[ref="tocToggle"]');
    const panel = this.refs.tocMobile || this.querySelector('[ref="tocMobile"]');
    if (!toggleBtn || !panel) return;

    toggleBtn.setAttribute('aria-expanded', 'false');
    panel.style.maxHeight = '0px';
    panel.classList.remove('is-open');
  }

}

if (!customElements.get('kc-article-toc')) {
  customElements.define('kc-article-toc', KcArticleToc);
}

/* -------------------------------------------------------------------------- */
/* Self-contained add-to-cart for the article-page product cards.             */
/* Bound at module load on `document` (independent of the custom element       */
/* lifecycle) so a failed element upgrade can never silently disable it.      */
/* Adds to cart, refreshes the cart drawer content, and opens the drawer      */
/* using its own handlers rather than relying on the theme event plumbing.    */
/* -------------------------------------------------------------------------- */

const KC_ATC_LOG = '[kc-article ATC]';

/**
 * Find every cart-items-component (the cart drawer's line-item wrappers).
 * @returns {HTMLElement[]}
 */
function kcCartItemsComponents() {
  return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('cart-items-component')));
}

/**
 * Replace each live cart-items-component's markup with the freshly rendered
 * section HTML returned by /cart/add.js, so the drawer reflects the new item.
 * @param {HTMLElement[]} liveComponents
 * @param {Record<string, string>|undefined} sections
 */
function kcUpdateCartDrawer(liveComponents, sections) {
  if (!sections) {
    console.warn(`${KC_ATC_LOG} no sections in add response — drawer content not refreshed`);
    return;
  }
  liveComponents.forEach((live) => {
    const id = live.dataset.sectionId;
    const html = id ? sections[id] : undefined;
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh =
      parsed.querySelector(`cart-items-component[data-section-id="${id}"]`) ||
      parsed.querySelector('cart-items-component');
    if (fresh) live.innerHTML = fresh.innerHTML;
  });
}

/**
 * Open the cart drawer, trying the component API first and falling back to the
 * native trigger button and finally the raw <dialog>.
 * @returns {boolean} whether the drawer was opened
 */
function kcOpenCartDrawer() {
  const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
  if (!drawer) {
    console.warn(`${KC_ATC_LOG} cart-drawer-component not found in DOM`);
    return false;
  }

  if (typeof drawer.open === 'function') {
    try {
      drawer.open();
      return true;
    } catch (error) {
      console.warn(`${KC_ATC_LOG} drawer.open() threw`, error);
    }
  }

  const trigger = /** @type {HTMLElement|null} */ (drawer.querySelector('[data-testid="cart-drawer-trigger"]'));
  if (trigger) {
    trigger.click();
    return true;
  }

  const dialog = /** @type {HTMLDialogElement|null} */ (drawer.querySelector('dialog'));
  if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
    dialog.showModal();
    return true;
  }

  console.warn(`${KC_ATC_LOG} could not open the cart drawer (no strategy worked)`);
  return false;
}

/**
 * Add the button's variant to the cart, refresh the drawer and open it.
 * @param {HTMLButtonElement} button
 */
async function kcProcessAdd(button) {
  const variantId = button.dataset.variantId;
  if (button.disabled || !variantId) return;
  button.disabled = true;

  try {
    const liveComponents = kcCartItemsComponents();
    const sectionIds = [
      ...new Set(liveComponents.map((el) => el.dataset.sectionId).filter(Boolean)),
    ];

    const addBody = /** @type {Record<string, unknown>} */ (
      applyPropertiesToPayload({ items: [{ id: Number(variantId), quantity: 1 }] }, resolveProperties(button))
    );
    if (sectionIds.length) addBody.sections = sectionIds.join(',');

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addBody),
    });
    const addData = await addResponse.json();

    if (addData.status) {
      console.warn(`${KC_ATC_LOG} add failed:`, addData.message || addData.description);
      return;
    }

    // Refresh the drawer's line items directly from the returned section HTML.
    kcUpdateCartDrawer(liveComponents, addData.sections);

    // Fetch the cart so anything listening (e.g. the header count bubble) can update.
    const cart = await (await fetch('/cart.js')).json();
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'kc-article-product-card',
          data: { source: 'kc-article-product-card', sections: addData.sections },
        },
      })
    );

    // Open the drawer ourselves.
    kcOpenCartDrawer();

    // Brief success feedback (mirrors the cart drawer's bestseller button).
    const originalText = button.textContent;
    button.dataset.added = 'true';
    button.textContent = '✓';
    setTimeout(() => {
      delete button.dataset.added;
      button.textContent = originalText;
    }, 2000);
  } catch (error) {
    console.error(`${KC_ATC_LOG} add-to-cart error`, error);
  } finally {
    button.disabled = false;
  }
}

/**
 * Capture-phase click handler. Runs before any app-injected handler so the
 * global cart app can't preempt it (via stopPropagation). Scoped to the KC
 * article product lists so it ignores the cart drawer's own bestseller buttons,
 * which share the `.product-card-mini__add-btn` class but are handled by
 * assets/cart-drawer-bestsellers.js.
 * @param {MouseEvent} event
 */
function kcHandleClickCapture(event) {
  const target = /** @type {HTMLElement} */ (event.target);
  const button = /** @type {HTMLButtonElement | null} */ (
    target?.closest?.('.product-card-mini__add-btn')
  );
  if (!button || !button.closest('.kc-article__products')) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  kcProcessAdd(button);
}

if (!window.__kcArticleAtcBound) {
  window.__kcArticleAtcBound = true;
  document.addEventListener('click', kcHandleClickCapture, true);
}
